import { Menu } from "@base-ui/react/menu";
import { Separator } from "@base-ui/react/separator";
import type { FunctionComponent, MouseEvent, ReactElement, ReactNode } from "react";
import { cn } from "./cn";
import type { PopupTrigger } from "./popover";
import { POPUP_TRANSITION, POPUP_Z_INDEX, toPositioning, type Placement } from "./popup";

/** antd's `MenuInfo`, reduced to what the app reads. */
export interface MenuClickInfo {
  key: string;
  domEvent: MouseEvent<HTMLElement>;
}

export interface DropdownActionItem {
  key: string;
  label: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  onClick?: (info: MenuClickInfo) => void;
}

export interface DropdownDividerItem {
  type: "divider";
  key?: string;
}

export type DropdownItem = DropdownActionItem | DropdownDividerItem;

/**
 * Same shape as antd's `MenuProps` so `menu={{ items, onClick }}` and `MenuProps["items"]` swap over
 * unchanged. Item `onClick` runs first, then the menu-level `onClick`. Groups and submenus are not
 * supported.
 */
export interface MenuProps {
  items?: DropdownItem[];
  onClick?: (info: MenuClickInfo) => void;
}

export interface DropdownProps {
  menu: MenuProps;
  /** Defaults to "hover", as in antd. An antd-style array (`["click"]`) is accepted too. */
  trigger?: PopupTrigger | PopupTrigger[];
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** Set to false when the child is not a native `<button>` (e.g. a `<div>` or `<a>`). */
  nativeButton?: boolean;
  /** Popup class. */
  className?: string;
  /** The trigger — must accept a ref and spread props. */
  children: ReactElement;
}

const isDivider = (item: DropdownItem): item is DropdownDividerItem => "type" in item && item.type === "divider";

/** Action menu anchored to a single child (Base UI `Menu`), with antd's `menu` prop shape. */
export const Dropdown: FunctionComponent<DropdownProps> = ({
  menu,
  trigger = "hover",
  placement = "bottomLeft",
  open,
  onOpenChange,
  disabled,
  nativeButton = true,
  className,
  children,
}) => {
  const { side, align } = toPositioning(placement);
  const openOnHover = ([] as PopupTrigger[]).concat(trigger).includes("hover");

  return (
    <Menu.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined} disabled={disabled}>
      <Menu.Trigger openOnHover={openOnHover} delay={100} nativeButton={nativeButton} render={children} />
      <Menu.Portal>
        <Menu.Positioner side={side} align={align} sideOffset={4} className={POPUP_Z_INDEX}>
          <Menu.Popup
            className={cn(
              "min-w-32 rounded-lg bg-noa-elevated p-1 text-sm text-noa shadow-lg shadow-noa-shadow outline-none",
              POPUP_TRANSITION,
              className
            )}
          >
            {menu.items?.map((item, index) => {
              if (isDivider(item)) {
                return <Separator key={item.key ?? `divider-${index}`} className="my-1 h-px bg-noa-border" />;
              }

              return (
                <Menu.Item
                  key={item.key}
                  disabled={item.disabled}
                  onClick={(event) => {
                    const info = { key: item.key, domEvent: event as MouseEvent<HTMLElement> };
                    item.onClick?.(info);
                    menu.onClick?.(info);
                  }}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded px-3 py-[5px] outline-none select-none",
                    "data-[disabled]:cursor-not-allowed data-[disabled]:text-noa-disabled",
                    item.danger
                      ? "text-noa-danger data-[highlighted]:bg-noa-danger data-[highlighted]:text-noa-on-danger"
                      : "data-[highlighted]:bg-noa-hover"
                  )}
                >
                  {item.icon}
                  <span className="min-w-0 flex-1">{item.label}</span>
                </Menu.Item>
              );
            })}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
};
